import { createContext, useContext, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  NegotiationRoundStatus,
  useListBidsForEventQuery,
  useListNegotiationRoundsQuery,
  type NegotiationRoundDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { awardedStage } from "./awardedStage";
import { supportsNegotiationRounds } from "./supportsNegotiationRounds";

export const ORIGINAL_COMPETITION = "original";

interface RoundSelection {
  rounds: NegotiationRoundDto[];
  roundsLoaded: boolean;
  roundsFailed: boolean;
  retryRounds: () => void;
  rejectedOrganizationIds: string[];
  /** null = the original competition (round 0). */
  selectedRound: NegotiationRoundDto | null;
  latestSentRound: NegotiationRoundDto | null;
  currentRound: NegotiationRoundDto | null;
  tenderRound: NegotiationRoundDto | null;
  selectRound: (round: NegotiationRoundDto | null) => void;
}

const NO_ROUNDS: RoundSelection = {
  rounds: [],
  roundsLoaded: true,
  roundsFailed: false,
  retryRounds: () => undefined,
  rejectedOrganizationIds: [],
  selectedRound: null,
  latestSentRound: null,
  currentRound: null,
  tenderRound: null,
  selectRound: () => undefined,
};

const RoundSelectionContext = createContext<RoundSelection>(NO_ROUNDS);

/**
 * ADR-033 D1: round selection is view-level context. The `?round=` param holds a round id or
 * "original"; absent means where the competition stands — the latest sent round, or once
 * awarded, the stage the winning bid was delivered in.
 */
export const RoundSelectionProvider = ({
  event,
  children,
}: {
  event: SourcingEventDto;
  children: ReactNode;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const readsRounds = supportsNegotiationRounds(event);
  const { data, isSuccess, isError, refetch } = useListNegotiationRoundsQuery(
    readsRounds ? { eventId: event.id } : skipToken,
  );
  const rounds = [...(data?.rounds ?? [])].sort((a, b) => a.roundNumber - b.roundNumber);
  const latestSentRound = rounds.filter((round) => round.status !== NegotiationRoundStatus.Draft).at(-1) ?? null;

  const winningBidIds = event.award?.winningBidIds ?? [];
  const { data: bidsData } = useListBidsForEventQuery(
    rounds.length > 0 && winningBidIds.length > 0 ? { eventId: event.id } : skipToken,
  );
  const awardedRound = awardedStage(winningBidIds, bidsData?.bids, rounds);
  const landingRound = awardedRound === undefined ? latestSentRound : awardedRound;

  const param = searchParams.get("round");
  const selectedRound =
    param === ORIGINAL_COMPETITION ? null : rounds.find((round) => round.id === param) ?? landingRound;

  const tenderRound = selectedRound?.status === NegotiationRoundStatus.Draft ? latestSentRound : selectedRound;

  const selectRound = (round: NegotiationRoundDto | null) => {
    const next = new URLSearchParams(searchParams);
    next.set("round", round?.id ?? ORIGINAL_COMPETITION);
    setSearchParams(next, { replace: true });
  };

  return (
    <RoundSelectionContext.Provider
      value={{
        rounds,
        roundsLoaded: !readsRounds || isSuccess,
        roundsFailed: readsRounds && isError,
        retryRounds: refetch,
        rejectedOrganizationIds: data?.rejectedOrganizationIds ?? [],
        selectedRound,
        latestSentRound,
        currentRound: landingRound,
        tenderRound,
        selectRound,
      }}
    >
      {children}
    </RoundSelectionContext.Provider>
  );
};

export const useRoundSelection = (): RoundSelection => useContext(RoundSelectionContext);

/**
 * ADR-033 D2: the award works on the effective final stage — the latest COMPLETED round
 * when rounds exist, else the original competition (null). Pinned, never pager-driven.
 */
export const useFinalStageRound = (): NegotiationRoundDto | null => {
  const { rounds } = useRoundSelection();
  const completed = rounds.filter((round) => round.status === NegotiationRoundStatus.Completed);
  return completed[completed.length - 1] ?? null;
};
